import { change } from 'redux-form';
import type {
	CardAction,
	CardData,
	CardFlowEvent,
	StoryModel,
	StorySettingsType,
	StoryStep,
	StoryVersionType,
} from 'types/story';
import { UPDATE_EDITABLE_STORY } from 'admin/constants/actions';
import { AUTO_SYNC_TARGET, FORM_MODEL } from 'admin/constants/common';
import type { AdminReducerState, AdminThunkDispatch } from 'admin/reducers';

/**
 * Local write into the story editor.
 *
 * Autosave (`storyEditorSync`) sends `form.storyEditor.values` to the PUT endpoints
 * (main row + latest version), not `storyEditor.story`.
 * Any write that did not already come from redux-form itself must set `updateForm: true`.
 * Otherwise UI can look updated while the next request posts stale form values.
 *
 * `updateForm: false` is correct only when the form is already the source of the change
 * (example: `StorySettingsModal` inputs mutated the form; this action only mirrors
 * `storyEditor` so `meta.autoSync` can fire).
 *
 * `entire: true` replaces the whole `story` and skips the form update (`path` becomes undefined).
 * Do not use it unless the form already holds the same `StoryModel`.
 */
export type EditableStoryData =
	| Partial<StoryModel>
	| Partial<StoryVersionType>
	| StoryModel[keyof StoryModel]
	| CardData
	| Partial<CardAction>
	| CardFlowEvent[]
	| StoryStep[]
	| NonNullable<StorySettingsType['variables']>['custom'];

export type Payload = {
	/** When true, also write `form.storyEditor` at the same path. Required for chat / programmatic edits. */
	updateForm?: boolean;
	/** Replace entire `StoryModel`. Skips form update. */
	entire?: boolean;
	/** Lodash path under the story (or under the current version when `isVersionPath` is true). */
	path?: string;
	/**
	 * Default true: prefix `path` with `storyVersions.<currentVersion>`.
	 * False for root fields (`name` / `type` / `language`).
	 */
	isVersionPath?: boolean;
	/**
	 * Value written at `path` via lodash `set`.
	 * Whole `StoryModel` (`entire`), `StoryVersionType` slice, or a nested field
	 * (root `name`/`type`/`language`, card, steps, events, settings fragment, …).
	 */
	data: EditableStoryData;
};

type StoryEditorChangePayload = {
	data: Payload['data'];
	path?: string;
};

/**
 * Update storyEditor reducer.
 */
export const storyEditorChange = (payload: StoryEditorChangePayload) =>
	({ type: UPDATE_EDITABLE_STORY.UPDATE, payload }) as const;

/**
 * Update form.storyEditor reducer.
 */
export const storyEditorFormChange = ({ data, path }: { data: Payload['data']; path: string }) =>
	change(FORM_MODEL.EDIT_STORY, path, data);

/**
 * Update current story version, a field on it, a root story field, or the entire story.
 * Triggers `AUTO_SYNC_TARGET.STORY_EDITOR` after the reducer write.
 */
export function updateEditableStory({
	data,
	path = '',
	entire = false,
	updateForm = false,
	isVersionPath = true,
}: Payload) {
	return (dispatch: AdminThunkDispatch, getState: () => AdminReducerState): void => {
		const version = getState().version.current;

		const pathPattern = isVersionPath ? `storyVersions.${version}${path ? `.${path}` : ''}` : path;
		const _path = entire ? undefined : pathPattern;

		if (updateForm && _path !== undefined) {
			dispatch(storyEditorFormChange({ data, path: _path }));
		}

		dispatch({
			...storyEditorChange({ data, path: _path }),
			meta: { autoSync: AUTO_SYNC_TARGET.STORY_EDITOR },
		});
	};
}
